import type { NextRouter } from 'next/router';
import { useRouter } from 'next/router';
import React from 'react';

interface Props {
  children: React.ReactNode;
  renderErrorScreen: (error?: Error) => React.ReactNode;
  onError?: (error: Error) => void;
}

interface PropsWithRouter extends Props {
  router: NextRouter;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorPathname?: string;
}

function isHydrationError(error: Error) {
  const msg = error.message || '';
  return /hydration|Minified React error #418|Minified React error #423|Minified React error #425/i.test(msg);
}

class ErrorBoundary extends React.PureComponent<PropsWithRouter, State> {
  state: State = {
    hasError: false,
  };

  static getDerivedStateFromError(error: Error) {
    // React 18 recovers from hydration mismatches; don't show the 500 screen for them.
    if (isHydrationError(error)) {
      return null;
    }

    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props: PropsWithRouter, state: State) {
    if (state.hasError && state.errorPathname) {
      if (props.router.pathname !== state.errorPathname) {
        return { hasError: false, error: undefined, errorPathname: undefined };
      }
    }

    return null;
  }

  componentDidCatch(error: Error) {
    if (isHydrationError(error)) {
      return;
    }

    this.setState({ hasError: true, error, errorPathname: this.props.router.pathname });
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.renderErrorScreen(this.state.error);
    }

    return this.props.children;
  }
}

const WrappedErrorBoundary = (props: Props) => {
  const router = useRouter();
  return <ErrorBoundary { ...props } router={ router }/>;
};

export default React.memo(WrappedErrorBoundary);
